// library functions
import { ColorOverlayFilter } from "Filters/ColorOverlayFilter";
import { TimelineLite } from "gsap/TimelineLite";
import { TimelineMax } from "gsap/TimelineMax";
import { TweenLite } from "gsap/TweenLite";
import { TweenMax } from "gsap/TweenMaxBase";
import { makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

// hlf stuff
import * as HLF from "html-living-framework";
import { AnimatedSprite, AssetFactory, AssetMapData, AssetType, SpriteAnimation } from "html-living-framework";
import {addBG, addSFX, addVO} from "Utils/AssetUtils";

// static data
import * as Constants from "Data/Constants";
import { GameConfig } from "Data/GameConfig";
import type { ItemID, LocationID } from "Data/GameConfig";
import type { IHiddenItemConfig } from "Interfaces/IHiddenItemConfig";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";
import type { ILocationConfig } from "Interfaces/ILocationConfig";
import type { IPayoffConfig } from "Interfaces/IPayoffConfig";
import { IVoiceoverConfig } from "Interfaces/IVoiceoverConfig";

// pubsub topics
import * as Topics from "Data/Topics";

// gameobjects
import { InteriorBackground } from "GameObjects/GameInterior/InteriorBackground";
import { InteriorHUD } from "GameObjects/GameInterior/InteriorHUD";
import { InteriorItemsListReturnItems } from "GameObjects/GameInterior/ReturnItems/InteriorItemsListReturnItems";
import { InteriorLunaReturnItems } from "GameObjects/GameInterior/ReturnItems/InteriorLunaReturnItems";
import { InteriorNPCReturnItems } from "GameObjects/GameInterior/ReturnItems/InteriorNPCReturnItems";
import { InteriorReceptaclesReturnItems } from "GameObjects/GameInterior/ReturnItems/InteriorReceptaclesReturnItems";

// singletons
import { Application } from "Application";

// #region private constants
const fnt = Constants.FONT_GEL_MED_22;
const ss = Constants.SPRITESHEET_UI;
const STATE_GAME_INTERIOR: string = "State_GameInteriorReturnItems";
const IDLE_DELAY = 10;
const IDLE_REPEAT_DELAY = 10;
// #endregion

export class GameInteriorReturnItems extends HLF.State {

// #region static properties
    public static readonly Assets: HLF.AssetMapData[] = [
        new HLF.AssetMapData(fnt, AssetType.FONT),
        new HLF.SpineAsset("luna", HLF.AssetType.SPINE_JSON, "luna"),
        ...ss.map((s) => new HLF.AssetMapData(s, AssetType.TEXTURE_ATLAS)),
        new HLF.AudioAsset("level-success", HLF.AudioCategory.SFX),
    ];
//#endregion

// #region children
    protected bg = this.addChild(new InteriorBackground());
    protected hud = this.addChild(new InteriorHUD());
    protected itemsList = this.addChild(new InteriorItemsListReturnItems());
    protected receptacles = this.addChild(new InteriorReceptaclesReturnItems());
    protected luna = this.addChild(new InteriorLunaReturnItems());
    protected npc = this.addChild(new InteriorNPCReturnItems());
    protected payoffContainer = this.addChild(new PIXI.Container());
    protected particleBurst = this.addChild(new AnimatedSprite());
// #endregion

// #region local state
    protected _subscriptions: any[] = [];
    protected payoffConfig!: IPayoffConfig;
    protected id!: LocationID;
    protected vo?: IVoiceoverConfig;
    protected doCheerSound: boolean = false;
    protected bgm?: HLF.IAudioTrack;
    protected idleTimeline?: TimelineMax;
    protected state?: ILevelState;
// #endregion

// #region temporary or debug variables
// #endregion

// #region static methods
    public static GetIdForState(id: LocationID, narrative: number, variation: number) {
        return `${STATE_GAME_INTERIOR}_${id}_${narrative}_${variation}`;
    }

    public static GetAssetsForState(pID: LocationID, narrative: number, variation: number): HLF.AssetMapData[] {
        const locationConfig = GameConfig.locations[pID];
        const narrativeConfig = locationConfig.narratives[narrative];
        const narrativeVariationConfig = narrativeConfig.variations[variation];

        const interior = locationConfig.interior;
        const itemslist = {...interior.itemslist.return, ...narrativeVariationConfig.itemslist?.return};
        const vo = {...narrativeVariationConfig.vo, ...narrativeConfig.vo, ...locationConfig.vo};
        const hiddenitems = narrativeVariationConfig.hiddenitems as IHiddenItemConfig[];
        const itemIDs: ItemID[] = hiddenitems.map((h) => (typeof(h.item) === "string" ? h.item : h.item.id) as ItemID);

        const assets: AssetMapData[] = [];

        // Particles
        assets.push(...AssetFactory({"@1x": 1, "@2x": [1, 3]}, (i) => new HLF.TextureAtlasAsset(`magicpoof-${i}`)));
        // VO
        addVO(assets, vo.return_items);
        addVO(assets, vo.all_items_returned);
        // Background
        addBG(pID, assets, interior.bg, HLF.AssetType.JPG);
        // music
        assets.push(new HLF.AudioAsset(locationConfig.bgm, HLF.AudioCategory.BGM));

        // Spritesheet contains Hiding Spots, Hidden Items, and NPC
        assets.push(
            ...HLF.AssetFactory(GameConfig.spritesheet_factory[pID], (i) => new HLF.TextureAtlasAsset(`${pID}-${i}`)));

        if (Array.isArray(interior.itemslist.return.itemslots)) {
            interior.itemslist.return.itemslots.forEach((s) => {
                addSFX(pID, assets, s.dropSFX);
            });
        }
        else {
            addSFX(pID, assets, interior.itemslist.return.itemslots.dropSFX);
        }

        if (itemslist.doCheerSound) {
            addSFX(pID, assets, "crowd-cheer-start");
            addSFX(pID, assets, "crowd-cheer");
        }

        itemIDs.forEach((id: ItemID, i) => {
            const item = locationConfig.items[id];
            addVO(assets, item.vo?.returned);
        });

        return assets;
    }
// #endregion

    public init(pSize: PIXI.Point, pData: ILevelState) {
        super.init(pSize, pData); // this calls onResize
        this.state = pData;
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);

        const config = GameConfig.locations[pData.id];

        this.setChildIndex(this.bg,         0);
        this.setChildIndex(this.receptacles,  1);
        this.setChildIndex(this.payoffContainer, 2);
        this.setChildIndex(this.itemsList,  3);
        this.setChildIndex(this.npc,        4);
        this.setChildIndex(this.luna,       5);
        this.setChildIndex(this.hud,        6);

        this.bg.init(pSize, pData);
        this.itemsList.init(pSize, pData);
        this.receptacles.init(pSize, pData);
        this.luna.init(pSize, pData);
        this.npc.init(pSize, pData);
        this.hud.init(pSize, pData);

        this.initVoiceover(pData);
        this.initMusic(pData);
        this.initParticles();
        this.initPayoff(pData, config);

        this._subscriptions.push(
            PubSub.subscribe(Topics.ITEM_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ALL_ITEMS_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.PLAY_PARTICLES, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.LEVEL_COMPLETE, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.SHOW_CONTINUE_BUTTON, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.RESTART_IDLE_TIMER, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.GAME_PAUSED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.GAME_UNPAUSED, this.onMessageReceived.bind(this)),
        );

        // console.log("please return your items", pData);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.bg.onResize(pSize);
        this.itemsList.onResize(pSize);
        this.receptacles.onResize(pSize);
        this.hud.onResize(pSize);
        this.npc.onResize(pSize);
        this.luna.onResize(pSize);
    }

    public animateIn(pOnComplete: () => void) {
        this.bg.animateIn();
        this.receptacles.animateIn();
        this.hud.animateIn();
        this.npc.animateIn();
        this.luna.animateIn();

        this.bgm?.setLooped(true);
        this.bgm?.setVolume(0.125);
        if (!(this.bgm?.isPlaying())) {
            this.bgm?.play();
        }

        super.animateIn(pOnComplete);

        const onVoComplete = () => {
            this.idleTimeline = new TimelineMax();
            this.idleTimeline.delay(IDLE_DELAY);
            this.idleTimeline.call(() => {
                const voToPlay = HLF.ArrayUtils.random(
                    this.state?.allItemsReturned ?
                     (this.vo?.all_items_returned_idle ?? []) :
                     (this.vo?.return_items_idle ?? []),
                );

                if (voToPlay !== undefined) {
                    this.idleTimeline?.kill();
                    Application.instance.voiceover.playVO(
                        [voToPlay, Constants.VO_SPACING],
                        HLF.PlayMode.New,
                        () => { this.RestartIdleTimer(); },
                    );
                }
            }, undefined, undefined, IDLE_REPEAT_DELAY);
            this.idleTimeline.repeat(-1);
        };

        TweenMax.delayedCall(1, () => {
            this.luna.HatOff();
            this.itemsList.animateIn({
                getHatPosition: this.luna.getHatPosition.bind(this.luna),
                onDoneWithHat: () => { // TODO: PubSub
                    this.luna.HatOn();
                },
            });

            if (this.vo?.return_items === undefined || Application.instance.skipVO) {
                onVoComplete();
            }
            else {
                Application.instance.voiceover.playVO(this.vo.return_items, HLF.PlayMode.Override, onVoComplete);
            }
        });
    }

    public update(pDeltaTime: number): void {
        super.update(pDeltaTime);
        this.luna.update(pDeltaTime);
        if (this.particleBurst.isPlaying) {
            this.particleBurst.visible = true;
            this.particleBurst.update(pDeltaTime);
        }
        else {
            this.particleBurst.visible = false;
        }
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        TweenLite.killTweensOf(this);
        this.idleTimeline?.kill();
        super.destroy(pOptions);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.LEVEL_COMPLETE:
                this.OnLevelCompleted(pData as ILevelState);
                break;
            case Topics.ITEM_RETURNED:
                this.OnItemReturned(pData as IItemState);
                break;
            case Topics.ALL_ITEMS_RETURNED:
                this.OnAllItemsReturned(pData as ILevelState);
                break;
            case Topics.PLAY_PARTICLES:
                this.PlayParticles(pData as ILevelState);
                break;
            case Topics.SHOW_CONTINUE_BUTTON:
                this.ShowContinueButton(pData as ILevelState);
                break;
            case Topics.RESTART_IDLE_TIMER:
                this.RestartIdleTimer();
                break;
            case HLF.GAME_PAUSED:
                this.OnGamePaused();
                break;
            case HLF.GAME_UNPAUSED:
                this.OnGameUnpaused();
                break;
        }
    }

    protected OnGamePaused() {
        this.idleTimeline?.kill();
    }

    protected OnGameUnpaused() {
        this.RestartIdleTimer();
    }

    protected RestartIdleTimer() {
        this.idleTimeline?.restart(true, true);
    }

    protected OnItemReturned(state: IItemState) {
        // console.log("item returned: ", state);
        this.state!.items[state.id] = state;

        this.RestartIdleTimer();

        if (this.payoffConfig.sprite !== undefined) {
            if (Array.isArray(this.payoffConfig.sprite)) {
                const filename = this.payoffConfig.sprite.shift();
                this.payoffConfig.sprite.push(filename!);
                const payoff = makeSprite(this.id + "/" + filename, GameConfig.spritesheets[this.id]);
                this.payoffContainer.addChild(payoff);

                const filter = new ColorOverlayFilter(0xffffff, 1);
                payoff.filters = [filter];
                filter.padding = 8;

                new TimelineLite()
                    .to(filter, 0.5, {mix: 0})
                    .call(() => payoff.filters = [])
                ;
            }
        }
    }

    protected OnAllItemsReturned(state: ILevelState) {
        // console.log("all items returned. ", state);
        this.state = state;
        this.idleTimeline?.kill();

        Application.instance.audio.play("level-success", 0.5, false, HLF.AudioCategory.SFX);

        if (this.doCheerSound) {
            Application.instance.audio.play("crowd-cheer-start", 0.5, false, HLF.AudioCategory.SFX);
            TweenLite.delayedCall(3, () => {
                Application.instance.audio.play("crowd-cheer", 0.5, true, HLF.AudioCategory.SFX);
            });
        }

        if (this.payoffConfig.sprite !== undefined) {
            if (!Array.isArray(this.payoffConfig.sprite)) {
                const payoff = makeSprite(state.id + "/" + this.payoffConfig.sprite, GameConfig.spritesheets[this.id]);
                this.payoffContainer.addChild(payoff);
            }
        }
/*
        else {
            const payoff = new PIXI.Graphics();
            payoff.beginFill(0xffffff);
            payoff.drawRoundedRect(-100, -100, 200, 200, 10);
            payoff.endFill();
            payoff.addChild(makeText("Payoff FPO\n" + payoffConfig.name, fnt, 22));
            this.payoffContainer.addChild(payoff);
        }
*/

        const filter = new ColorOverlayFilter(0xffffff, 1);
        this.payoffContainer.filters = [filter];
        filter.padding = 8;

        new TimelineLite()
            .fromTo(this.payoffContainer, 0.5,
                {pixi: { scale: 0, alpha: 0 }},
                {pixi: { scale: 1, alpha: 1 }},
            )
            .to(filter, 0.5, {mix: 0})
            .call(() => this.payoffContainer.filters = [])
            .call(() => {
                if (this.vo?.all_items_returned) {
                    Application.instance.voiceover.playVO(this.vo.all_items_returned, HLF.PlayMode.Override, () => {
                        this.RestartIdleTimer();
                    });
                }
                else {
                    this.RestartIdleTimer();
                }
            })
        ;
    }

    protected PlayParticles(state: ILevelState) {
        this.particleBurst.play("magicpoof");
    }

    protected ShowContinueButton(state: ILevelState) {
        this.RestartIdleTimer();
    }

    protected OnLevelCompleted(state: ILevelState) {
        // console.log("level completed: ", state);
        if (this.doCheerSound) {
            Application.instance.audio.fadeTo("crowd-cheer", HLF.AudioCategory.SFX, 0, 500);
        }
        this.RestartIdleTimer();
    }

    protected initPayoff(pData: ILevelState, config: ILocationConfig) {
        this.id = pData.id;
        this.payoffConfig = config.payoffs[pData.payoff];
        if (this.payoffConfig.position !== undefined) {
            this.payoffContainer.position.set(this.payoffConfig.position.x, this.payoffConfig.position.y);
        }
        this.doCheerSound = config.interior.itemslist.return.doCheerSound ?? false;
        // if (Array.isArray(this.payoffConfig.sprite)) {
        //     this.setChildIndex(this.payoffContainer, this.getChildIndex(this.itemsList));
        // }
    }

    protected initParticles() {
        const magicPoof = new SpriteAnimation(
            "-e-magicpoof-", // prefix
            ["magicpoof-1", "magicpoof-2", "magicpoof-3"], // spritesheet(s)
            22, // total frames
            0, // zero-padding
            15, // frames per second
            false, // loop
            1, // first frame
        );
        this.particleBurst.scale.set(2);
        this.particleBurst.addAnimation("magicpoof", magicPoof);
    }

    protected initVoiceover(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        const narrativeConfig = GameConfig.locations[state.id].narratives[state.narrative];
        const narrativeVariationConfig =
            GameConfig.locations[state.id].narratives[state.narrative].variations[state.narrativeVariation];

        this.vo = {...locationConfig.vo, ...narrativeConfig.vo, ...narrativeVariationConfig.vo};
    }

    protected initMusic(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        this.bgm = Application.instance.audio.getAudioTrack(locationConfig.bgm, HLF.AudioCategory.BGM);
    }
}
